import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Wallet, HandCoins } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const METHODS = ['cash', 'venmo', 'zelle', 'check', 'other'];

export default function AdminStreetTeamWallets() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null); // wallet row being paid
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [working, setWorking] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/street-team/wallets`, { headers });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const [checking, setChecking] = useState(false);

  const checkConnect = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${API}/admin/street-team/stripe-connect-check`, {}, { headers });
      if (r.data.ok) toast.success(`${r.data.message} (${r.data.mode} mode)`);
      else toast.error(`${r.data.message} (${r.data.mode} mode)`, { duration: 12000 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Check failed'); }
    finally { setChecking(false); }
  };

  const togglePayouts = async (enabled) => {
    try {
      await axios.post(`${API}/admin/street-team/payouts-toggle`, { enabled }, { headers });
      toast.success(enabled ? 'Self-serve payouts are now OPEN' : 'Self-serve payouts closed');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const openPay = (w) => {
    setPaying(w);
    setAmount(((w.balance_cents || 0) / 100).toFixed(2));
    setMethod('cash');
    setNote('');
  };

  const recordPayout = async () => {
    const cents = Math.round(parseFloat(amount || '0') * 100);
    if (!cents || cents <= 0) { toast.error('Enter a valid amount'); return; }
    setWorking(true);
    try {
      await axios.post(`${API}/admin/street-team/manual-payout`, {
        user_id: paying.user_id, amount_cents: cents, method, note,
      }, { headers });
      toast.success(`Recorded ${usd(cents)} ${method} payout`);
      setPaying(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payout failed'); }
    finally { setWorking(false); }
  };

  if (!data) return <div className="min-h-screen bg-background pt-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const owed = data.wallets.filter((w) => (w.balance_cents || 0) > 0);
  const totalOwed = owed.reduce((s, w) => s + (w.balance_cents || 0), 0);

  return (
    <div className="min-h-screen bg-background pt-20 pb-20" data-testid="admin-street-team-wallets">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Wallet className="h-6 w-6" /> Promoter Wallets</h1>
          <div className="flex items-center gap-2 text-sm">
            <Button size="sm" variant="outline" onClick={checkConnect} disabled={checking} data-testid="stripe-connect-check-btn">
              {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Test Stripe Connect
            </Button>
            <Label htmlFor="payouts-live" className="text-xs text-muted-foreground">Self-serve payouts</Label>
            <Switch id="payouts-live" checked={!!data.payouts_live} onCheckedChange={togglePayouts} data-testid="payouts-live-toggle" />
            <Badge variant="outline" className={data.payouts_live ? 'border-emerald-500/50 text-emerald-400' : 'border-amber-500/50 text-amber-300'}>
              {data.payouts_live ? 'OPEN' : 'Closed — manual payouts only'}
            </Badge>
          </div>
        </div>

        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm"><strong>{owed.length}</strong> promoter{owed.length === 1 ? '' : 's'} with a balance to pay</p>
            <p className="text-lg font-bold text-amber-300" data-testid="total-owed">Total owed: {usd(totalOwed)}</p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="p-3">Promoter</th>
                  <th className="p-3 text-right">Balance</th>
                  <th className="p-3 text-right">Pending</th>
                  <th className="p-3 text-right">Lifetime earned</th>
                  <th className="p-3 text-right">Paid out</th>
                  <th className="p-3">Last payout</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {data.wallets.map((w) => (
                  <tr key={w.user_id} className="border-b border-border/50" data-testid={`wallet-row-${w.user_id}`}>
                    <td className="p-3">
                      <p className="font-medium">{w.name || w.user_id}</p>
                      <p className="text-xs text-muted-foreground">{w.email}</p>
                    </td>
                    <td className="p-3 text-right font-bold text-emerald-400">{usd(w.balance_cents)}</td>
                    <td className="p-3 text-right text-amber-300">{usd(w.pending_cents)}</td>
                    <td className="p-3 text-right">{usd(w.lifetime_earned_cents)}</td>
                    <td className="p-3 text-right">{usd(w.lifetime_paid_cents)}</td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {w.last_payout
                        ? <>{usd(Math.abs(w.last_payout.amount_cents))} · {w.last_payout.method}{w.last_payout.type === 'manual_payout' ? ' (manual)' : ''}<br />{(w.last_payout.created_at || '').slice(0, 10)}</>
                        : '—'}
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" disabled={(w.balance_cents || 0) <= 0}
                        onClick={() => openPay(w)} data-testid={`manual-payout-btn-${w.user_id}`}>
                        <HandCoins className="h-3.5 w-3.5 mr-1" /> Record payout
                      </Button>
                    </td>
                  </tr>
                ))}
                {data.wallets.length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No promoter wallets yet.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!paying} onOpenChange={(o) => !o && setPaying(null)}>
        <DialogContent className="max-w-sm" data-testid="manual-payout-dialog">
          <DialogHeader><DialogTitle>Record manual payout</DialogTitle></DialogHeader>
          {paying && (
            <div className="space-y-3">
              <p className="text-sm">{paying.name || paying.user_id} · balance <strong className="text-emerald-400">{usd(paying.balance_cents)}</strong></p>
              <div className="space-y-1.5">
                <Label>Amount paid (USD)</Label>
                <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="manual-payout-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>How was it paid?</Label>
                <div className="flex flex-wrap gap-1.5">
                  {METHODS.map((m) => (
                    <button key={m} type="button" onClick={() => setMethod(m)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border ${method === m ? 'bg-emerald-600 text-white border-emerald-600' : 'border-border text-muted-foreground hover:bg-muted'}`}
                      data-testid={`method-${m}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. paid at venue after close" data-testid="manual-payout-note" />
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={recordPayout} disabled={working} data-testid="manual-payout-confirm">
                {working ? <Loader2 className="h-4 w-4 animate-spin" /> : `Record ${usd(Math.round(parseFloat(amount || '0') * 100))} payout`}
              </Button>
              <p className="text-[11px] text-muted-foreground">Deducts from their balance and shows in their transaction history. Cannot exceed the current balance.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
